import React, { FormEventHandler, useState } from 'react'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import {
  FrameworkSectionsCSVForm,
  csvRowsToSectionDrafts,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsCSVForm'
import { ImportFrameworkSectionsCSVRow } from 'src/components/ModalForms/Frameworks/AddFramework/FrameworkSectionsCSV.constants'
import { getCsvDuplicateMessages } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.helpers'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import {
  useAddFrameworkSections,
  useExistingFrameworkNames,
  useFrameworkControls,
} from 'src/pages/Frameworks/frameworks.hooks'
import { ModalForm, ModalFormId } from '../..'

type Props = {
  framework: ComplianceFramework
}

export const AddFrameworkSectionsCSVModal = ({
  framework,
}: Props): JSX.Element => {
  const cfModelId = framework.getModelId()
  const show = useIsShowModal(ModalFormId.ADD_FRAMEWORK_SECTIONS_CSV)
  const hide = useHideModal({ modalId: ModalFormId.ADD_FRAMEWORK_SECTIONS_CSV })
  const addFrameworkSections = useAddFrameworkSections()

  const {
    existingSectionNames,
    existingRequirementNames,
    isLoadingSections: sectionsLoading,
  } = useExistingFrameworkNames(framework, show)
  const {
    validControlModelIdSet,
    controlIdByModelId,
    isLoading: controlsLoading,
  } = useFrameworkControls(show)

  const [csvItems, setCsvItems] = useState<ImportFrameworkSectionsCSVRow[]>([])
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvErrors, setCsvErrors] = useState<string[]>([])
  const [showAttachmentError, setShowAttachmentError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const closeAndReset = () => {
    setCsvItems([])
    setCsvFile(null)
    setCsvErrors([])
    setShowAttachmentError(false)
    setIsSubmitting(false)
    hide()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!csvFile) {
      setShowAttachmentError(true)
      return
    }
    if (csvErrors.length > 0) return

    const sectionsToSubmit = csvRowsToSectionDrafts(
      csvItems,
      controlIdByModelId,
    )
    const dupeMessages = getCsvDuplicateMessages(
      sectionsToSubmit,
      existingSectionNames,
      existingRequirementNames,
    )
    if (dupeMessages.length > 0) {
      setCsvErrors(dupeMessages)
      return
    }

    setIsSubmitting(true)
    try {
      await addFrameworkSections({
        complianceFrameworkModelId: cfModelId,
        sections: sectionsToSubmit,
      })
      toast.success('Sections added.')
      closeAndReset()
    } catch (err) {
      const errorMessage = getErrorMessage(err as RpcError)
      setCsvErrors([errorMessage.message])
      if (errorMessage.shouldLog) {
        log.error('AddFrameworkSectionsCSVModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.ADD_FRAMEWORK_SECTIONS_CSV}
      size="xl"
      title="Add Sections"
      submitText="Add sections"
      showOverflow
      disableSubmitButton={
        isSubmitting ||
        sectionsLoading ||
        controlsLoading ||
        !csvFile ||
        csvErrors.length > 0
      }
    >
      <form id={ModalFormId.ADD_FRAMEWORK_SECTIONS_CSV} onSubmit={onSubmit}>
        <FrameworkSectionsCSVForm
          existingRequirementNames={existingRequirementNames}
          existingSectionNames={existingSectionNames}
          validControlModelIdSet={validControlModelIdSet}
          isSubmitting={isSubmitting}
          isLoadingControls={controlsLoading}
          showAttachmentError={showAttachmentError}
          externalErrors={csvErrors}
          onParseChange={(parsed) => {
            setCsvItems(parsed.items)
            setCsvFile(parsed.file)
            setCsvErrors(parsed.errors)
            if (parsed.file) setShowAttachmentError(false)
          }}
        />
      </form>
    </ModalForm>
  )
}
